import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Panel } from '../../components/Panel'
import { publicationName } from '../../lib/format'
import { errorDetail } from '../../lib/visitorsFormat'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { VisitorsHeader } from './VisitorsHeader'

const wrap = 'p-8 space-y-6 max-w-[1500px]'

/** Shared chrome for the drill-down pages: unknown-title guard, header with crumbs and range. */
export function VisitorsFrame({ pub, valid, title, crumbs, right, children }: {
  pub: string
  valid: boolean
  title: string
  crumbs: { label: string; to?: string }[]
  right?: ReactNode
  children: ReactNode
}) {
  const { range, search, setRange } = useVisitorsRange()
  if (!valid) {
    return (
      <div className={wrap}>
        <Panel accent="red">
          <p className="text-paper">Unknown title</p>
          <Link to="/visitors" className="text-amber-glow hover:underline font-mono text-[12px]">Back to all titles</Link>
        </Panel>
      </div>
    )
  }
  const name = publicationName(pub)
  return (
    <div className={wrap}>
      <VisitorsHeader
        pub={pub}
        title={title}
        crumbs={[{ label: 'Visitors', to: `/visitors?${search}` }, { label: name, to: `/visitors/${pub}?${search}` }, ...crumbs]}
        range={range}
        onRange={setRange}
        right={right}
      />
      {children}
    </div>
  )
}

/** An API failure: a 404 reads "Not found" with a way back, anything else shows the server's message. */
export function QueryProblem({ error, pub }: { error: unknown; pub: string }) {
  const { search } = useVisitorsRange()
  if ((error as { status?: number } | null)?.status === 404) {
    return (
      <Panel accent="red">
        <p className="text-paper">Not found</p>
        <Link to={`/visitors/${pub}?${search}`} className="text-amber-glow hover:underline font-mono text-[12px]">Back to {publicationName(pub)}</Link>
      </Panel>
    )
  }
  return <Panel accent="red"><p className="font-mono text-[12px] text-radar-red">{errorDetail(error)}</p></Panel>
}

export const Skeleton = ({ h = 'h-48' }: { h?: string }) => <div className={`${h} bg-ink-800 animate-pulse`} />

export const th = 'label text-left font-normal px-3 py-2'
export const thr = 'label text-right font-normal px-3 py-2'
export const tdn = 'px-3 py-2 text-right'
export const linkCls = 'text-amber-glow hover:underline'
